import { useState, useEffect, useRef, useMemo, useCallback, startTransition } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Radio } from 'lucide-react';
import { startOfDay, endOfDay, format, differenceInHours } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { HeaderControls } from '@/components/dashboard/HeaderControls';
import { DeviceMap, type DeviceStatusFilter } from '@/components/dashboard/DeviceMap';
import { DeviceStatusChart } from '@/components/dashboard/DeviceStatusChart';
import { LastCommunicationCard, type CommunicationFilter } from '@/components/dashboard/LastCommunicationCard';
import { AlarmsCard } from '@/components/dashboard/AlarmsCard';
import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

import { PositionHistoryBell } from '@/components/dashboard/PositionHistoryBell';
import { ActivityChart } from '@/components/dashboard/ActivityChart';
import { useTraccarWebSocket } from '@/hooks/useTraccarWebSocket';
import { useRealtimeActivity } from '@/hooks/useRealtimeActivity';
import {
  fetchDevices,
  fetchPositions,
  fetchGroups,
  fetchSummary,
  fetchTrips,
  type Position,
  type Event,
  type Summary,
  type Trip,
  type Stop
} from '@/lib/traccar-api';
import { useResellerAccess } from '@/hooks/useResellerAccess';
import { fetchDeviceMerges } from '@/lib/reseller-api';

// Haversine formula to calculate distance between two coordinates in meters
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371000; // Earth's radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const Index = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // WebSocket for real-time updates
  const {
    positions: wsPositions,
    devices: wsDevices,
    events: wsEvents,
    isConnected: wsConnected,
    connect: wsConnect,
    disconnect: wsDisconnect,
  } = useTraccarWebSocket();

  // Real-time activity tracking
  const { processPositionUpdates, initializeFromPositions } = useRealtimeActivity();
  const activityInitializedRef = useRef(false);

  const lastWsPositionSigByDeviceRef = useRef<Map<number, string>>(new Map());
  const lastPositionByDeviceRef = useRef<Map<number, Position>>(new Map());

  // React Query for cached data fetching
  const { data: initialDevices = [], isLoading: devicesLoading } = useQuery({
    queryKey: ['devices'],
    queryFn: fetchDevices
  });

  const { data: initialPositions = [], isLoading: positionsLoading } = useQuery({
    queryKey: ['positions'],
    queryFn: fetchPositions,
    staleTime: 10000,
    gcTime: 5 * 60 * 1000,
  });

  const { data: groups = [] } = useQuery({
    queryKey: ['groups'],
    queryFn: fetchGroups,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Fetch summaries when devices are loaded
  const { data: initialSummaries = [] } = useQuery({
    queryKey: ['summaries', initialDevices.map(d => d.id).join(',')],
    queryFn: async () => {
      if (initialDevices.length === 0) return [];
      const fromDate = format(startOfDay(new Date()), "yyyy-MM-dd'T'HH:mm:ss'Z'");
      const toDate = format(endOfDay(new Date()), "yyyy-MM-dd'T'HH:mm:ss'Z'");
      return fetchSummary(initialDevices.map(d => d.id), fromDate, toDate);
    },
    enabled: initialDevices.length > 0,
    staleTime: 60000,
    gcTime: 5 * 60 * 1000,
  });


  // Merge cached data with WebSocket updates
  const devices = useMemo(() => {
    if (wsDevices.length === 0) return initialDevices;
    const deviceMap = new Map(initialDevices.map(d => [d.id, d]));
    wsDevices.forEach(d => deviceMap.set(d.id, d));
    return Array.from(deviceMap.values());
  }, [initialDevices, wsDevices]);

  const { isReseller } = useResellerAccess();
  const { data: deviceMerges = [] } = useQuery({
    queryKey: ['device-merges'],
    queryFn: fetchDeviceMerges,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
  const mergeSecondaryIds = useMemo(() => {
    const set = new Set<number>();
    deviceMerges.forEach(m => set.add(m.secondary_device_id));
    return set;
  }, [deviceMerges]);
  const visibleDevices = useMemo(() => {
    if (isReseller) return devices;
    return devices.filter(d => !mergeSecondaryIds.has(d.id));
  }, [devices, isReseller, mergeSecondaryIds]);

  const [positions, setPositions] = useState<Position[]>([]);
  const [positionHistory, setPositionHistory] = useState<Position[]>([]);
  const [focusPosition, setFocusPosition] = useState<Position | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [summaries, setSummaries] = useState<Summary[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [stops, setStops] = useState<Stop[]>([]);
  const [activityChartTrips, setActivityChartTrips] = useState<Trip[]>([]);
  const liveTripsRef = useRef(trips);
  const liveStopsRef = useRef(stops);
  const devicesRef = useRef(devices);
  devicesRef.current = devices;
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [statusFilter, setStatusFilter] = useState<DeviceStatusFilter>(null);
  const [communicationFilter, setCommunicationFilter] = useState<CommunicationFilter>(null);
  const [searchParams] = useSearchParams();
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(() => {
    const param = searchParams.get('deviceId');
    return param ? Number(param) : null;
  });
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showChartsRow, setShowChartsRow] = useState<boolean>(() => {
    const saved = localStorage.getItem('dashboard.showChartsRow');
    return saved !== null ? saved === 'true' : true;
  });

  const toggleChartsRow = () => {
    setShowChartsRow(prev => {
      const newValue = !prev;
      localStorage.setItem('dashboard.showChartsRow', String(newValue));
      return newValue;
    });
  };

  // Initialize positions from a cache
  useEffect(() => {
    if (initialPositions.length > 0 && positions.length === 0) {
      setPositions(initialPositions);
      initialPositions.forEach(pos => {
        lastPositionByDeviceRef.current.set(pos.deviceId, pos);
      });
    }
  }, [initialPositions, positions.length]);

  // Initialize summaries from cache
  useEffect(() => {
    if (initialSummaries.length > 0 && summaries.length === 0) {
      setSummaries(initialSummaries);
    }
  }, [initialSummaries, summaries.length]);

  // Fetch trips sequentially, one device at a time, with per-device caching
  const tripsFetchedRef = useRef<string | null>(null);
  const todayFrom = useMemo(() => format(startOfDay(new Date()), "yyyy-MM-dd'T'HH:mm:ss'Z'"), []);
  const todayTo = useMemo(() => format(endOfDay(new Date()), "yyyy-MM-dd'T'HH:mm:ss'Z'"), []);

  const { data: cachedTrips, isFetching: isFetchingTrips } = useQuery({
    queryKey: ['trips-all', initialDevices.map(d => d.id).join(','), todayFrom],
    queryFn: async () => {
      const allTrips: Trip[] = [];
      setTrips([]);
      setActivityChartTrips([]);
      liveTripsRef.current = [];
      tripsFetchedRef.current = initialDevices.map(d => d.id).join(',');

      for (const device of initialDevices.slice(0, 30)) {
        try {
          const deviceTrips = await queryClient.fetchQuery({
            queryKey: ['trips', device.id, todayFrom, todayTo],
            queryFn: () => fetchTrips(device.id, todayFrom, todayTo),
            staleTime: 10 * 60000,
          });
          if (deviceTrips.length > 0) {
            allTrips.push(...deviceTrips);
            setTrips(prev => {
              const updated = [...prev, ...deviceTrips];
              liveTripsRef.current = updated;
              return updated;
            });
          }
        } catch (error) {
          console.error(`Error fetching trips for device ${device.id}:`, error);
        }
      }

      return allTrips;
    },
    enabled: initialDevices.length > 0,
    staleTime: 60000,
    gcTime: 5 * 60 * 1000,
  });

  // Initialize from the cache when navigating back
  useEffect(() => {
    const deviceKey = initialDevices.map(d => d.id).join(',');
    if (cachedTrips && !isFetchingTrips && tripsFetchedRef.current !== deviceKey) {
      setTrips(cachedTrips);
      liveTripsRef.current = cachedTrips;
      setActivityChartTrips(cachedTrips);
      tripsFetchedRef.current = deviceKey;
    }
  }, [cachedTrips, isFetchingTrips, initialDevices]);

  // Initialize activity tracker from initial positions
  useEffect(() => {
    if (initialPositions.length > 0 && !activityInitializedRef.current) {
      initializeFromPositions(initialPositions);
      activityInitializedRef.current = true;
    }
  }, [initialPositions, initializeFromPositions]);

  // Filter devices by search query first
  const searchFilteredDevices = useMemo(() => {
    if (!searchQuery.trim()) return visibleDevices;
    const q = searchQuery.toLowerCase();
    return visibleDevices.filter(d => d.name.toLowerCase().includes(q));
  }, [visibleDevices, searchQuery]);

  // Filter devices by group first
  const groupFilteredDevices = selectedGroupId
    ? searchFilteredDevices.filter(d => d.groupId === selectedGroupId)
    : searchFilteredDevices;

  // Filter devices and positions based on selected device (within group filter)
  const filteredDevices = selectedDeviceId
    ? groupFilteredDevices.filter(d => d.id === selectedDeviceId)
    : groupFilteredDevices;
  const effectivePositions = useMemo(() => {
    if (isReseller) return positions;
    const posMap = new Map(positions.map(p => [p.deviceId, p]));
    deviceMerges.forEach(merge => {
      const primaryPos = posMap.get(merge.primary_device_id);
      const secondaryPos = posMap.get(merge.secondary_device_id);
      if (secondaryPos) {
        const primaryTime = primaryPos ? new Date(primaryPos.fixTime).getTime() : 0;
        const secondaryTime = new Date(secondaryPos.fixTime).getTime();
        if (secondaryTime > primaryTime) {
          posMap.set(merge.primary_device_id, { ...secondaryPos, deviceId: merge.primary_device_id });
        }
      }
    });
    return Array.from(posMap.values());
  }, [positions, isReseller, deviceMerges]);

  const filteredPositions = useMemo(() => {
    const deviceIds = new Set(filteredDevices.map(d => d.id));
    return effectivePositions.filter(p => deviceIds.has(p.deviceId));
  }, [effectivePositions, filteredDevices]);
  const filteredPositionHistory = useMemo(() => {
    const deviceIds = new Set(filteredDevices.map(d => d.id));
    return positionHistory.filter(p => deviceIds.has(p.deviceId));
  }, [positionHistory, filteredDevices]);

  // Apply chart filters (status + communication) for map and distance chart

  // Cross-filtered devices for each chart (filtered by the OTHER chart's filter)
  const statusChartDevices = useMemo(() => {
    if (!communicationFilter) return filteredDevices;
    const now = new Date();
    return filteredDevices.filter(device => {
      if (communicationFilter === 'noposition') return !device.positionId;
      if (!device.positionId) return false;
      if (!device.lastUpdate) return communicationFilter === 'critical';
      const hoursAgo = differenceInHours(now, new Date(device.lastUpdate));
      switch (communicationFilter) {
        case 'recent': return hoursAgo < 1;
        case 'warning': return hoursAgo >= 1 && hoursAgo < 24;
        case 'critical': return hoursAgo >= 24;
        default: return true;
      }
    });
  }, [filteredDevices, communicationFilter]);

  const statusChartPositions = useMemo(() => {
    const deviceIds = new Set(statusChartDevices.map(d => d.id));
    return filteredPositions.filter(p => deviceIds.has(p.deviceId));
  }, [filteredPositions, statusChartDevices]);

  const commChartDevices = useMemo(() => {
    if (!statusFilter) return filteredDevices;
    const positionMap = new Map(filteredPositions.map(p => [p.deviceId, p]));
    return filteredDevices.filter(device => {
      const pos = positionMap.get(device.id);
      const isOnline = device.status === 'online';
      const isMoving = pos && pos.speed > 0;
      const ignition = pos?.attributes?.ignition as boolean | undefined;
      switch (statusFilter) {
        case 'moving': return isOnline && !!isMoving;
        case 'idle': return isOnline && !isMoving && !!ignition;
        case 'parked': return isOnline && !isMoving && !ignition;
        case 'offline': return !isOnline;
        default: return true;
      }
    });
  }, [filteredDevices, filteredPositions, statusFilter]);

  const handleActivityDeviceSelect = useCallback((deviceId: number | null) => {
    setSelectedDeviceId(deviceId);
    setStatusFilter(null);
  }, []);

  const isLoading = devicesLoading || positionsLoading;
  const hasLoaded = devices.length > 0 || positions.length > 0;

  useEffect(() => {

    wsConnect();

    return () => {
      wsDisconnect();
    };
  }, [navigate, wsConnect, wsDisconnect]);

  // Flush live trips/stops to state every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setTrips(prev => prev !== liveTripsRef.current ? liveTripsRef.current : prev);
      setStops(prev => prev !== liveStopsRef.current ? liveStopsRef.current : prev);
    }, 10_000);
    return () => clearInterval(interval);
  }, []);

  // Flush trips to ActivityChart every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActivityChartTrips(prev => prev !== liveTripsRef.current ? liveTripsRef.current : prev);
    }, 5_000);
    return () => clearInterval(interval);
  }, []);

  // Handle WebSocket positions - update positions, increment summary distances, and update activity
  useEffect(() => {
    if (wsPositions.length > 0) {
      setPositions(prev => {
        const updated = new Map(prev.map(p => [p.deviceId, p]));
        wsPositions.forEach(p => updated.set(p.deviceId, p));
        return Array.from(updated.values());
      });

      // Track position history changes and calculate distance increments
      const sigByDevice = lastWsPositionSigByDeviceRef.current;
      const lastPosMap = lastPositionByDeviceRef.current;
      const newUpdates: Position[] = [];
      const distanceIncrements = new Map<number, number>();

      wsPositions.forEach((wsPos) => {
        const sig = `${wsPos.fixTime}-${wsPos.latitude}-${wsPos.longitude}-${wsPos.speed}-${wsPos.course ?? ''}`;
        const prevSig = sigByDevice.get(wsPos.deviceId);

        if (prevSig !== sig) {
          newUpdates.push(wsPos);
          sigByDevice.set(wsPos.deviceId, sig);

          // Calculate distance from last known position
          const lastPos = lastPosMap.get(wsPos.deviceId);
          if (lastPos && wsPos.valid) {
            const distance = calculateDistance(
              lastPos.latitude, lastPos.longitude,
              wsPos.latitude, wsPos.longitude
            );
            // Only add if distance is reasonable (< 10km between updates, > 5m to filter noise)
            if (distance > 5 && distance < 10000) {
              distanceIncrements.set(wsPos.deviceId, distance);
            }
          }

          // Update last known position
          lastPosMap.set(wsPos.deviceId, wsPos);
        }
      });

      // Defer non-critical updates so they don't block the position render
      startTransition(() => {
        // Update summaries with distance increments
        if (distanceIncrements.size > 0) {
          setSummaries(prev => {
            return prev.map(summary => {
              const increment = distanceIncrements.get(summary.deviceId);
              if (increment) {
                return { ...summary, distance: summary.distance + increment };
              }
              return summary;
            });
          });
        }

        // Update activity timeline in ref (flushed to state on interval)
        if (newUpdates.length > 0) {
          const { trips: updatedTrips, stops: updatedStops } = processPositionUpdates(
            newUpdates,
            devicesRef.current,
            liveTripsRef.current,
            liveStopsRef.current
          );
          liveTripsRef.current = updatedTrips;
          liveStopsRef.current = updatedStops;

          newUpdates.sort(
            (a, b) => new Date(b.fixTime).getTime() - new Date(a.fixTime).getTime()
          );
          setPositionHistory(prev => [...newUpdates, ...prev].slice(0, 300));
        }

        setLastUpdate(new Date());
      });
    }
  }, [wsPositions, processPositionUpdates]);

  useEffect(() => {
    if (wsEvents.length > 0) {
      setEvents(prev => [...wsEvents, ...prev].slice(0, 100));
    }
  }, [wsEvents]);

  if (isLoading && !hasLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading fleet data...</p>
        </div>
      </div>
    );
  }

  const headerContent = (
    <HeaderControls
      lastUpdate={lastUpdate}
      devices={groupFilteredDevices}
      groups={groups}
      selectedDeviceId={selectedDeviceId}
      selectedGroupId={selectedGroupId}
      onDeviceFilterChange={setSelectedDeviceId}
      onGroupFilterChange={setSelectedGroupId}
      showChartsRow={showChartsRow}
      onToggleChartsRow={toggleChartsRow}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
    />
  );

  const headerActions = (
    <div className="flex items-center gap-2">
      {lastUpdate && (
        <span className="text-xs text-muted-foreground hidden md:inline">
          {t('dashboard.lastUpdate')}: {format(lastUpdate, 'HH:mm:ss')}
        </span>
      )}
      <Badge variant={wsConnected ? "default" : "secondary"} className="gap-1 hidden md:flex">
        <Radio className={cn("h-3 w-3", wsConnected && "animate-pulse")} />
        {wsConnected ? "Live" : "Offline"}
      </Badge>
      <div className="hidden sm:block">
        <PositionHistoryBell
          positionHistory={filteredPositionHistory}
          devices={filteredDevices}
          events={events}
          onPositionClick={setFocusPosition}
        />
      </div>
    </div>
  );

  return (
    <DashboardLayout headerContent={headerContent} headerActions={headerActions}>
      <div className="p-6 h-full flex flex-col gap-6">
        {showChartsRow && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 [&>*]:max-h-[180px]">
            <DeviceStatusChart
              devices={statusChartDevices}
              positions={statusChartPositions}
              activeFilter={statusFilter}
              onFilterChange={setStatusFilter}
            />
            <LastCommunicationCard
              devices={commChartDevices}
              activeFilter={communicationFilter}
              onFilterChange={setCommunicationFilter}
            />
            <div className="hidden md:block">
              <AlarmsCard devices={filteredDevices} positions={filteredPositions} />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 flex-1">
            <ActivityChart
              trips={activityChartTrips}
              devices={groupFilteredDevices}
              positions={effectivePositions}
              onDeviceSelect={handleActivityDeviceSelect}
              selectedDeviceId={selectedDeviceId}
            />
          <div className="lg:col-span-2 min-h-[200px] rounded-xl overflow-hidden">
            <DeviceMap
              devices={filteredDevices}
              positions={filteredPositions}
              focusPosition={focusPosition}
              statusFilter={statusFilter}
              communicationFilter={communicationFilter}
              selectedDeviceId={selectedDeviceId}
              onDeviceSelect={(deviceId) => {
                setSelectedDeviceId(deviceId)
                setStatusFilter(null)
              }}
            />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Index;
